import { useEffect } from "react";

import { useSubscriptionConfigStore, getDefaultStripePrice } from "~/lib/stores/subscription-config";
import { useUserStore } from "~/lib/stores/user";
import { formatPriceCents, getUserCurrency } from "~/lib/utils";

import bannerImg21 from "~/assets/deal-feed/banner-2-1.svg";
import bannerImg22 from "~/assets/deal-feed/banner-2-2.svg";
import bannerImg23 from "~/assets/deal-feed/banner-2-3.svg";
import bannerImg24 from "~/assets/deal-feed/banner-2-4.svg";
import blidzQrImg from "~/assets/blidz-qr.png";
import bannerPhoneImg from "~/assets/deal-feed/banner-phone.svg";
import bannerBkgImg from "~/assets/deal-feed/banner_bkg.svg";
import membershipBannerIconImg from "~/assets/deal-feed/membership-banner-icon.svg";
import membershipIconImg from "~/assets/deal-feed/membership-badge.svg";
import shieldCheckIconImg from "~/assets/deal-feed/shield-check-icon.svg";
import shoppingBagIconImg from "~/assets/deal-feed/shopping-bag-icon.svg";
import appleIconImg from "~/assets/deal-feed/apple-icon.svg";
import googlePlayIconImg from "~/assets/deal-feed/google-play-icon.svg";
import trustpilotIconImg from "~/assets/deal-feed/trustpilot-icon.png";
import zapIconImg from "~/assets/deal-feed/zap.svg";
import { ChevronRight } from "lucide-react";

import bannerPhone1Img from "~/assets/deal-feed/banner-phone1.webp";
import bannerPhone2Img from "~/assets/deal-feed/banner-phone2.webp";
import bannerPhone3Img from "~/assets/deal-feed/banner-phone3.webp";

const MEMBERSHIP_READ_MORE_URL = "https://intercom.help/blidz/en/collections/3847845-blidz-membership";

export function Banner1() {
  const { user } = useUserStore();
  const { subscriptionConfig, fetchSubscriptionConfig } = useSubscriptionConfigStore();
  useEffect(() => {
    fetchSubscriptionConfig();
  }, [fetchSubscriptionConfig]);
  const currency = getUserCurrency(user);
  const defaultPrice = getDefaultStripePrice(subscriptionConfig);
  const priceCents = defaultPrice?.currencyOptions?.[currency.toLowerCase()]?.unitAmount ?? 2499;
  const priceLabel = `${formatPriceCents(priceCents, currency)}/month`;

  return (
    <div
      className="desktop-large:h-[368px] flex w-full justify-center overflow-hidden rounded-2xl border border-black/5 bg-cover bg-center shadow-none"
      style={{ backgroundImage: `url(${bannerBkgImg})` }}
    >
      <div className="relative flex h-auto w-full max-w-[1440px]">
        {/* Left side: Phone mockups — desktop only */}
        <div className="desktop-large:block relative hidden w-[42%] shrink-0">
          <img src={bannerPhoneImg} alt="" className="absolute top-[137.55px] left-[16%] z-1 w-[196.95px]" />
          <div className="absolute top-[137.55px] left-[16%] z-2 w-[196.95px] px-[7.3px] pt-[5.49px]">
            <div className="overflow-hidden rounded-[28.8px]">
              <img src={bannerPhone2Img} alt="" className="w-[182.39px]" />
            </div>
          </div>
          <img src={bannerPhoneImg} alt="" className="absolute top-[95.71px] left-[55%] z-3 w-[218.84px]" />
          <div className="absolute top-[95.71px] left-[55%] z-4 w-[218.84px] px-[9.65px] pt-[7.35px]">
            <div className="overflow-hidden rounded-[33.6px]">
              <img src={bannerPhone3Img} alt="" className="w-[201.46px]" />
            </div>
          </div>
          <img src={bannerPhoneImg} alt="" className="absolute top-[46.08px] left-[33%] z-5 w-[243.15px]" />
          <div className="absolute top-[46.08px] left-[33%] z-6 w-[243.15px] px-[9.65px] pt-[8.17px]">
            <div className="overflow-hidden rounded-[34.52px]">
              <img src={bannerPhone1Img} alt="" className="w-[223.85px]" />
            </div>
          </div>
        </div>

        {/* Right side: Membership info */}
        <div className="desktop-large:gap-[22px] desktop-large:py-9 desktop-large:pr-6 desktop-large:pl-0 relative z-10 flex flex-1 flex-col gap-4 p-5">
          {/* Title and benefits */}
          <div className="desktop-large:gap-6 flex flex-col gap-4">
            {/* Title */}
            <div className="flex flex-col gap-[3px]">
              <div className="flex items-center gap-[3px]">
                <img src={membershipBannerIconImg} alt="" className="desktop-large:size-[42px] size-7" />
                <span className="text-foreground desktop-large:text-[40px] desktop-large:leading-[44px] desktop-large:tracking-[-0.8px] text-xl leading-6 font-semibold tracking-[-0.4px]">
                  Blidz
                </span>
                <span className="text-foreground desktop-large:text-[40px] desktop-large:leading-[44px] desktop-large:tracking-[-0.8px] text-xl leading-6 font-normal tracking-[-0.4px]">
                  Plus
                </span>
              </div>
              <span className="text-foreground desktop-large:text-[40px] desktop-large:leading-[44px] desktop-large:tracking-[-0.8px] text-xl leading-6 font-semibold tracking-[-0.4px]">
                Membership only {priceLabel}
              </span>
            </div>

            {/* Benefits grid — desktop */}
            <div className="desktop-large:flex hidden flex-wrap gap-x-[52px] gap-y-3">
              <div className="flex flex-col gap-2">
                <BenefitItem icon={<img src={membershipIconImg} alt="" className="size-3.5" />}>
                  Free trial, then {priceLabel}
                </BenefitItem>
                <BenefitItem icon={<img src={shieldCheckIconImg} alt="" className="size-3.5" />}>
                  NEW: 14D unconditional refund
                </BenefitItem>
              </div>
              <div className="flex flex-col gap-2">
                <BenefitItem icon={<img src={zapIconImg} alt="" className="size-3.5" />}>Instant payout</BenefitItem>
                <BenefitItem icon={<img src={shoppingBagIconImg} alt="" className="size-3.5" />}>
                  Shopping discount
                </BenefitItem>
              </div>
            </div>

            {/* Benefits + ratings grid — narrow */}
            <div className="desktop-large:hidden flex flex-wrap gap-x-[52px] gap-y-3">
              <div className="flex flex-col gap-2">
                <BenefitItem icon={<img src={membershipIconImg} alt="" className="size-3.5" />}>
                  Free trial, then {priceLabel}
                </BenefitItem>
                <BenefitItem icon={<img src={shieldCheckIconImg} alt="" className="size-3.5" />}>
                  14D unconditional refund
                </BenefitItem>
              </div>
              <div className="flex flex-col gap-2">
                <RatingItem icon={<img src={appleIconImg} alt="" className="size-3.5" />}>App Store 4.4</RatingItem>
                <RatingItem icon={<img src={trustpilotIconImg} alt="" className="size-3.5 object-contain" />}>
                  Trustpilot 4.3
                </RatingItem>
              </div>
            </div>
          </div>

          {/* Divider — desktop only */}
          <div className="desktop-large:block hidden h-px w-full bg-black/10" />

          {/* Bottom: ratings, button, QR — desktop only */}
          <div className="desktop-large:flex hidden items-end justify-between">
            <div className="flex flex-col gap-[9px]">
              {/* App ratings */}
              <div className="flex items-center gap-3">
                <RatingItem icon={<img src={appleIconImg} alt="" className="size-3.5" />}>App Store 4.4</RatingItem>
                <RatingItem icon={<img src={googlePlayIconImg} alt="" className="size-3.5" />}>
                  Play Store 4.3
                </RatingItem>
                <RatingItem icon={<img src={trustpilotIconImg} alt="" className="size-3.5 object-contain" />}>
                  Trustpilot 4.3
                </RatingItem>
              </div>

              {/* Read more button */}
              <a
                href={MEMBERSHIP_READ_MORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-foreground inline-flex h-12 w-fit items-center gap-1 rounded-full px-5 py-2 text-base font-semibold text-white"
              >
                Read more
                <ChevronRight className="size-5" />
              </a>
            </div>

            {/* QR Code */}
            <img src={blidzQrImg} alt="Blidz QR Code" className="size-[81px] rounded-[11px] border border-black/10" />
          </div>

          {/* Read more button — narrow only */}
          <a
            href={MEMBERSHIP_READ_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-foreground desktop-large:hidden inline-flex h-10 w-fit items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-white"
          >
            Read more
            <ChevronRight className="size-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

function BenefitItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="flex shrink-0 -translate-y-px">{icon}</span>
      <span className="text-foreground text-sm leading-5 font-medium">{children}</span>
    </div>
  );
}

function RatingItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="flex shrink-0 -translate-y-px">{icon}</span>
      <span className="text-foreground text-sm leading-5 font-medium">{children}</span>
    </div>
  );
}

export function Banner2() {
  return (
    <div className="flex w-full justify-center rounded-[24px] border border-white bg-[linear-gradient(105.32deg,#F3F3F3_19.01%,#FFFFFF_41.22%)] p-[24px] shadow-[0px_0px_24px_0px_#0000000d]">
      <div className="flex w-full max-w-[1440px] flex-row items-center justify-between gap-4 overflow-x-auto">
        <div className="inline-flex shrink-0 items-center justify-start gap-3">
          <img src={bannerImg21} alt="" />
          <div className="w-24 justify-start text-sm leading-none font-bold text-[#0C4A6E] uppercase">
            Delivery
            <br />
            guarantee
          </div>
        </div>
        <div className="inline-flex shrink-0 items-center justify-start gap-3">
          <img src={bannerImg22} alt="" />
          <div className="w-24 justify-start text-sm leading-none font-bold text-[#59500A] uppercase">
            Quality
            <br />
            selection
          </div>
        </div>
        <div className="inline-flex shrink-0 items-center justify-start gap-3">
          <img src={bannerImg23} alt="" />
          <div className="w-24 justify-start text-sm leading-none font-bold text-[#115B11] uppercase">
            Security
            <br />
            and Privacy
          </div>
        </div>
        <div className="inline-flex shrink-0 items-center justify-start gap-3">
          <img src={bannerImg24} alt="" />
          <div className="w-24 justify-start text-sm leading-none font-bold text-[#491F07] uppercase">
            Returns
            <br />
            POLICY
          </div>
        </div>
      </div>
    </div>
  );
}
